import {
  Box,
  Flex,
  FlexProps,
  Icon,
  IconButton,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { RecipeDetailProduct } from '@diamond/shared/types';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
  DeleteOutlined,
} from '@mui/icons-material';

import { Image } from '../../../common';

type RecipeCartProductSelectProps<T extends Array<RecipeDetailProduct>> = {
  product: T;
  selectedProductIdx: number;
  handleNavigate: (productId: string) => void;
  handleDelete: () => void;
};

export function RecipeCartProductSelect<T extends Array<RecipeDetailProduct>>({
  product,
  selectedProductIdx,
  handleNavigate,
  handleDelete,
}: RecipeCartProductSelectProps<T>) {
  const isEveryProductSame = product.every((p) => p.id === product[0].id);
  const isPrevousButtonDisabled =
    product.length <= 1 || selectedProductIdx === 0 || isEveryProductSame;
  const isNextButtonDisabled =
    product.length <= 1 ||
    selectedProductIdx === product.length - 1 ||
    isEveryProductSame;

  return (
    <Flex alignItems="center" w="full" gap="2">
      <IconButton
        isDisabled={isPrevousButtonDisabled}
        icon={<Icon as={ChevronLeftOutlined} />}
        aria-label="Prev"
        size="xs"
        bg="transparent"
        color="blue"
        _hover={{
          bg: 'transparent',
        }}
        _disabled={{
          bg: 'transparent',
          color: 'gray.33',
          cursor: 'not-allowed',
        }}
        onClick={() => handleNavigate(product[selectedProductIdx - 1].id)}
      />
      <Box overflow="hidden" position="relative">
        <Flex
          transition="transform 0.5s"
          transform={`translateX(-${selectedProductIdx * 100}%)`}
          willChange="transform"
        >
          {product.map((p) => (
            <RecipeCartProductItem product={p} minW="0px" flex="0 0 100%" />
          ))}
        </Flex>
      </Box>
      <Spacer />
      <IconButton
        isDisabled={isNextButtonDisabled}
        icon={<Icon as={ChevronRightOutlined} />}
        aria-label="Prev"
        size="xs"
        bg="transparent"
        color="blue"
        _hover={{
          bg: 'transparent',
        }}
        _disabled={{
          bg: 'transparent',
          color: 'gray.33',
          cursor: 'not-allowed',
        }}
        onClick={() => handleNavigate(product[selectedProductIdx + 1].id)}
      />
      <IconButton
        ml="8"
        icon={<Icon as={DeleteOutlined} />}
        aria-label="Prev"
        size="xs"
        bg="transparent"
        color="blue"
        _hover={{
          bg: 'transparent',
        }}
        onClick={handleDelete}
      />
    </Flex>
  );
}

type RecipeCartProductItemProps<T extends RecipeDetailProduct> = {
  product: T;
} & FlexProps;

function RecipeCartProductItem<T extends RecipeDetailProduct>({
  product,
  ...props
}: RecipeCartProductItemProps<T>) {
  const isNotAvailable =
    product.stock === 0 || product.stock < product.minimum_order_quantity;
  return (
    <Flex {...props} gap="2" alignItems="center">
      <Box
        flex="0 0 70px"
        position="relative"
        w="70px"
        h="70px"
        border="1px solid"
        borderColor="gray.33"
        rounded="lg"
      >
        <Image
          src={product.product_image_thumbnails?.[0]}
          alt={product.title}
          style={{
            borderRadius: '0.5rem',
            objectFit: 'cover',
            padding: '0.5rem',
          }}
          fill
        />
      </Box>
      <VStack alignItems="flex-start" spacing="1" w="full" flex="1">
        <Text
          fontSize="sm"
          mt="0.5"
          textDecoration={isNotAvailable ? 'line-through' : undefined}
        >
          <Text as="span" fontWeight="bold">
            {product.brand_desc}
          </Text>{' '}
          {product.title}
        </Text>
        <Flex justifyContent="space-between" w="full" gap="4">
          <VStack alignItems="flex-start" spacing="0" flex="1 1 30%">
            <Text fontSize="2xs" as="span" color="gray.75">
              Harga Satuan
            </Text>
            <Text fontSize="sm" mt="0">
              {buyerCurrencyFormatter(product.price)}
            </Text>
          </VStack>
          <VStack alignItems="flex-start" spacing="0" flex="1 1 20%">
            <Text fontSize="2xs" as="span" color="gray.75">
              qty
            </Text>
            <Text fontSize="sm" mt="0">
              x{product.minimum_order_quantity / 1000}
              <Text as="span" fontSize="2xs" color="gray.75">
                /{product.sales_unit_desc}
              </Text>
            </Text>
          </VStack>
          <VStack alignItems="flex-start" spacing="0" flex="1 1 50%">
            <Text fontSize="2xs" as="span" color="gray.75">
              Total Harga
            </Text>
            <Text fontSize="sm" mt="0" fontWeight="semibold">
              {buyerCurrencyFormatter(product.subtotal)}
            </Text>
          </VStack>
        </Flex>
      </VStack>
    </Flex>
  );
}
